import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Upload, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { useCart } from '../context/CartContext';
import api from '../utils/api';

const Checkout = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { cartItems, getCartTotal, clearCart } = useCart();
  const [step, setStep] = useState(1); // 1: Datos, 2: Orden creada, 3: Comprobante subido
  const [orderNumber, setOrderNumber] = useState('');
  const [orderId, setOrderId] = useState('');
  const [comprobante, setComprobante] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    nombre_cliente: '',
    email: '',
    telefono: '',
    nombre_mascota: '',
    especie: '',
    notas: ''
  });

  const subtotal = getCartTotal();
  const iva = subtotal * 0.16;
  const total = subtotal + iva;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setComprobante(file);
    }
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const orderData = {
        items: cartItems.map(item => ({
          estudio_id: item.estudioId,
          nombre: item.nombre,
          precio: item.precio,
          cantidad: item.cantidad,
          is_urgente: item.isUrgente
        })),
        subtotal,
        iva,
        total,
        ...formData
      };

      const response = await api.post('/orders', orderData);
      setOrderNumber(response.data.numero_orden);
      setOrderId(response.data.id);
      setStep(2);
      toast({
        title: '¡Orden creada!',
        description: `Tu número de orden es: ${response.data.numero_orden}`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Error al crear la orden',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUploadComprobante = async (e) => {
    e.preventDefault();
    if (!comprobante) {
      toast({
        title: 'Error',
        description: 'Por favor selecciona un comprobante',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('comprobante', comprobante);

      await api.post(`/orders/${orderId}/comprobante`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setStep(3);
      clearCart();
      toast({
        title: '¡Comprobante enviado!',
        description: 'Tu orden está siendo procesada. Te contactaremos pronto.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Error al subir el comprobante',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (cartItems.length === 0 && step === 1) {
    navigate('/carrito');
    return null;
  }

  // Step 3: Confirmación final
  if (step === 3) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-2xl w-full shadow-2xl">
          <CardContent className="p-12 text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-6">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-[#1e3a8a] mb-4">
              ¡Orden Completada!
            </h2>
            <p className="text-xl text-gray-600 mb-2">
              Número de orden: <span className="font-bold text-[#1e3a8a]">{orderNumber}</span>
            </p>
            <p className="text-gray-600 mb-8">
              Hemos recibido tu comprobante de pago. Nos pondremos en contacto contigo para coordinar la toma de muestras.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/')}
                className="w-full bg-[#84cc16] hover:bg-[#65a30d] text-white py-6 text-lg"
              >
                Volver al Inicio
              </Button>
              <Button
                onClick={() => navigate('/estudios')}
                variant="outline"
                className="w-full border-[#1e40af] text-[#1e40af] hover:bg-[#1e40af] hover:text-white"
              >
                Ver Más Estudios
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 2: Subir comprobante
  if (step === 2) {
    return (
      <div className="min-h-screen bg-gray-50">
        <section className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] text-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Realizar Pago</h1>
            <p className="text-xl opacity-90">Orden #{orderNumber}</p>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 py-12">
          <Card className="shadow-xl mb-8">
            <CardHeader className="bg-blue-50">
              <CardTitle className="text-[#1e3a8a]">Instrucciones de Pago</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <ol className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="bg-[#1e40af] text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">1</span>
                  <div>
                    <p className="font-semibold text-gray-800">Realiza tu transferencia bancaria</p>
                    <div className="mt-2 bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-700"><strong>Banco:</strong> BBVA</p>
                      <p className="text-sm text-gray-700"><strong>Cuenta:</strong> 0123456789</p>
                      <p className="text-sm text-gray-700"><strong>CLABE:</strong> 012345678901234567</p>
                      <p className="text-sm text-gray-700"><strong>Beneficiario:</strong> MoraBioVet S.A. de C.V.</p>
                      <p className="text-lg font-bold text-[#1e3a8a] mt-2">
                        Monto a transferir: ${total.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                      </p>
                    </div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-[#1e40af] text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">2</span>
                  <div>
                    <p className="font-semibold text-gray-800">Toma captura de tu comprobante</p>
                    <p className="text-sm text-gray-600">Asegúrate de que se vea el número de referencia y el monto</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-[#1e40af] text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">3</span>
                  <div>
                    <p className="font-semibold text-gray-800">Sube tu comprobante aquí</p>
                    <p className="text-sm text-gray-600">Formatos aceptados: JPG, PNG, PDF</p>
                  </div>
                </li>
              </ol>
            </CardContent>
          </Card>

          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="text-[#1e3a8a]">Subir Comprobante de Pago</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUploadComprobante} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Comprobante de pago *
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#1e40af] transition-colors cursor-pointer">
                    <input
                      type="file"
                      id="comprobante"
                      accept="image/*,.pdf"
                      onChange={handleFileChange}
                      className="hidden"
                      required
                    />
                    <label htmlFor="comprobante" className="cursor-pointer">
                      <Upload className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm text-gray-600 mb-1">
                        {comprobante ? comprobante.name : 'Haz clic para subir tu comprobante'}
                      </p>
                      <p className="text-xs text-gray-500">PNG, JPG o PDF (Máx. 5MB)</p>
                    </label>
                  </div>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                  <p className="text-sm text-yellow-800">
                    <strong>Importante:</strong> Una vez que subas tu comprobante, procesaremos tu orden y nos pondremos en contacto contigo para programar la toma de muestras.
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#84cc16] hover:bg-[#65a30d] text-white py-6 text-lg"
                  disabled={isSubmitting || !comprobante}
                >
                  {isSubmitting ? 'Enviando...' : 'Confirmar Pago'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Step 1: Formulario de datos
  return (
    <div className="min-h-screen bg-gray-50">
      <section className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Checkout</h1>
          <p className="text-xl opacity-90">Completa tu orden</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Formulario */}
          <div className="lg:col-span-2">
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="text-[#1e3a8a]">Información del Cliente</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmitOrder} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre completo *
                      </label>
                      <Input
                        type="text"
                        name="nombre_cliente"
                        value={formData.nombre_cliente}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Correo electrónico *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Teléfono *
                    </label>
                    <Input
                      type="tel"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Información del Paciente</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nombre de la mascota *
                        </label>
                        <Input
                          type="text"
                          name="nombre_mascota"
                          value={formData.nombre_mascota}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Especie *
                        </label>
                        <select
                          name="especie"
                          value={formData.especie}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e40af]"
                          required
                        >
                          <option value="">Selecciona una opción</option>
                          <option value="Canino">Canino</option>
                          <option value="Felino">Felino</option>
                          <option value="Aves">Aves</option>
                          <option value="Reptiles">Reptiles</option>
                          <option value="Otros">Otros</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notas adicionales
                    </label>
                    <textarea
                      name="notas"
                      rows="3"
                      value={formData.notas}
                      onChange={handleChange}
                      placeholder="Información adicional que consideres importante..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e40af]"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#84cc16] hover:bg-[#65a30d] text-white py-6 text-lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Procesando...' : 'Continuar al Pago'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Resumen */}
          <div className="lg:col-span-1">
            <Card className="shadow-lg sticky top-4">
              <CardHeader>
                <CardTitle className="text-[#1e3a8a]">Resumen de Orden</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-4">
                  {cartItems.map((item) => (
                    <div key={item.itemId} className="flex justify-between text-sm">
                      <div className="flex-1">
                        <p className="font-medium">{item.nombre}</p>
                        <p className="text-gray-500">
                          {item.cantidad} x ${item.precio.toLocaleString('es-MX')}
                          {item.isUrgente && <span className="text-red-600 ml-1">(Urgente)</span>}
                        </p>
                      </div>
                      <p className="font-semibold">
                        ${(item.precio * item.cantidad).toLocaleString('es-MX')}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal:</span>
                    <span className="font-semibold">${subtotal.toLocaleString('es-MX')}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>IVA (16%):</span>
                    <span className="font-semibold">${iva.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between">
                    <span className="text-lg font-bold">Total:</span>
                    <span className="text-2xl font-bold text-[#1e3a8a]">
                      ${total.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                    </span>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <CreditCard className="h-5 w-5 text-[#1e40af] mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Método de pago</p>
                      <p className="text-sm text-gray-600">Transferencia bancaria</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
